import React, { useState, useEffect, useRef, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Map, Marker } from '@vis.gl/react-maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useTranslation } from 'react-i18next';
import AuthContext from '../context/AuthContext';
import {
  Box, Typography, Button, Alert, CircularProgress, Paper,
} from '@mui/material';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';

import { NAV_BG, AMBER } from '../theme';

function formatCountdown(expiresAt) {
  const diff = Math.max(0, new Date(expiresAt).getTime() - Date.now());
  const mins = Math.floor(diff / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// mode: 'discovering' | 'tracking' | 'ended'
export default function ParentBusTrackingPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { backendUrl, authTokens, refreshAccessToken } = useContext(AuthContext);
  const { t } = useTranslation();

  const urlToken = params.get('token');

  const [mode, setMode] = useState(urlToken ? 'tracking' : 'discovering');
  const [activeToken, setActiveToken] = useState(urlToken || null);
  const [sessionChecked, setSessionChecked] = useState(!!urlToken);

  const [location, setLocation] = useState(null);
  const [childStopPos, setChildStopPos] = useState(null);
  const [endedStatus, setEndedStatus] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [countdown, setCountdown] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [networkErrors, setNetworkErrors] = useState(0);

  const locationIntervalRef = useRef(null);
  const countdownRef = useRef(null);

  // React to urlToken arriving after mount (happens when ParentTrackingBanner
  // injects ?token=... into the URL while the user is already on this page).
  useEffect(() => {
    if (urlToken && mode !== 'tracking') {
      setActiveToken(urlToken);
      setMode('tracking');
      setSessionChecked(true);
    }
  }, [urlToken]); // eslint-disable-line

  // Session discovery — fallback polling while in 'discovering' mode with no URL token.
  // Primary auto-start is handled by ParentTrackingBanner injecting the token into the URL.
  useEffect(() => {
    if (mode !== 'discovering') return;
    if (urlToken) return;
    if (!authTokens) return;
    const checkSession = async () => {
      try {
        let access = authTokens.access;
        let res = await fetch(`${backendUrl}api/parent/tracking-session/`, {
          headers: { Authorization: 'Bearer ' + String(access) },
        });
        // The stored access token is short-lived (60 min) and commonly expired on a
        // reopened tab/bookmark — refresh once and retry rather than treating this
        // as "no active session".
        if (res.status === 401 && refreshAccessToken) {
          access = await refreshAccessToken();
          if (access) {
            res = await fetch(`${backendUrl}api/parent/tracking-session/`, {
              headers: { Authorization: 'Bearer ' + String(access) },
            });
          }
        }
        const data = res.ok ? await res.json() : null;
        setSessionChecked(true);
        const first = data?.status === 'active' && data?.sessions?.length > 0 ? data.sessions[0] : null;
        if (first) {
          setActiveToken(first.token);
          setMode('tracking');
        }
      } catch (_) {
        setSessionChecked(true);
      }
    };
    checkSession();
    const id = setInterval(checkSession, 10000);
    return () => clearInterval(id);
  }, [mode, urlToken, authTokens, backendUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Location polling — runs while in 'tracking' mode
  useEffect(() => {
    if (mode !== 'tracking' || !activeToken) return;
    setLocationLoading(true);
    setNetworkErrors(0);

    const poll = () => {
      fetch(`${backendUrl}api/parent/bus-location/?token=${activeToken}`, { cache: 'no-store' })
        .then(r => r.json().then(data => ({ ok: r.ok, data })))
        .then(({ ok, data }) => {
          setNetworkErrors(0);
          if (!ok || data.status !== 'active') {
            setEndedStatus(!ok ? 'invalid' : (data.status ?? 'invalid'));
            setMode('ended');
            clearInterval(locationIntervalRef.current);
            return;
          }
          if (data.latitude != null && data.longitude != null) {
            setLocation({ lat: data.latitude, lng: data.longitude });
          }
          if (data.child_stop_latitude != null && data.child_stop_longitude != null) {
            setChildStopPos({ lat: data.child_stop_latitude, lng: data.child_stop_longitude });
          }
          if (data.expires_at) setExpiresAt(data.expires_at);
        })
        .catch(() => {
          setNetworkErrors(n => {
            const next = n + 1;
            if (next >= 3) {
              setEndedStatus('unreachable');
              setMode('ended');
              clearInterval(locationIntervalRef.current);
            }
            return next;
          });
        })
        .finally(() => setLocationLoading(false));
    };

    poll();
    locationIntervalRef.current = setInterval(poll, 3000);
    return () => clearInterval(locationIntervalRef.current);
  }, [mode, activeToken, backendUrl]);

  useEffect(() => {
    if (!expiresAt) return;
    countdownRef.current = setInterval(() => setCountdown(formatCountdown(expiresAt)), 1000);
    return () => clearInterval(countdownRef.current);
  }, [expiresAt]);

  const resetToDiscovery = () => {
    setMode('discovering');
    setActiveToken(null);
    setLocation(null);
    setEndedStatus(null);
    setExpiresAt(null);
    setCountdown('');
    setNetworkErrors(0);
    setLocationLoading(false);
  };

  // Still waiting for first session check
  if (!sessionChecked && mode === 'discovering') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: AMBER }} />
      </Box>
    );
  }

  // No active session
  if (mode === 'discovering') {
    return (
      <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <DirectionsBusIcon sx={{ color: AMBER }} />
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, fontSize: '1.1rem' }}>
            {t('parent_bus_tracking_page.title')}
          </Typography>
        </Box>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography sx={{ fontSize: '3rem', mb: 1 }}>🚌</Typography>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, fontSize: '1.3rem', mb: 1 }}>
            {t('parent_bus_tracking_page.no_session')}
          </Typography>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', mb: 1 }}>
            {t('parent_bus_tracking_page.no_session_desc')}
          </Typography>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#94a3b8', fontSize: '0.8rem' }}>
            {t('parent_bus_tracking_page.checking')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Session ended
  if (mode === 'ended') {
    return (
      <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/parent/today')}
            sx={{ color: NAV_BG, fontWeight: 700, textTransform: 'none' }}
          >
            {t('parent_bus_tracking_page.back')}
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DirectionsBusIcon sx={{ color: AMBER }} />
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, fontSize: '1.1rem' }}>
              {t('parent_bus_tracking_page.title')}
            </Typography>
          </Box>
        </Box>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography sx={{ fontSize: '3rem', mb: 1 }}>🚌</Typography>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, fontSize: '1.4rem', mb: 1 }}>
            {t('parent_bus_tracking_page.tracking_ended')}
          </Typography>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', mb: 3 }}>
            {t(`parent_bus_tracking_page.status_${endedStatus}`, t('parent_bus_tracking_page.session_inactive'))}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={resetToDiscovery}
              sx={{ borderColor: NAV_BG, color: NAV_BG, textTransform: 'none', '&:hover': { background: 'rgba(15,45,94,0.06)' } }}
            >
              {t('parent_bus_tracking_page.watch_new')}
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/parent/today')}
              sx={{ background: NAV_BG, textTransform: 'none', '&:hover': { background: '#1a4080' } }}
            >
              {t('parent_bus_tracking_page.go_back')}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Tracking mode
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/parent/today')}
          sx={{ color: NAV_BG, fontWeight: 700, textTransform: 'none' }}
        >
          {t('parent_bus_tracking_page.back')}
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <DirectionsBusIcon sx={{ color: AMBER }} />
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, fontSize: '1.1rem' }}>
            {t('parent_bus_tracking_page.title')}
          </Typography>
        </Box>
        {countdown && (
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#64748b' }}>
            {t('parent_bus_tracking_page.expires_in', { countdown })}
          </Typography>
        )}
      </Box>

      {locationLoading && !location && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: AMBER }} />
        </Box>
      )}

      {!locationLoading && !location && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {t('parent_bus_tracking_page.waiting_location')}
        </Alert>
      )}

      {location && (
        <Box sx={{ height: 500, borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          <Map
            viewState={{ longitude: location.lng, latitude: location.lat, zoom: 15 }}
            onMove={() => {}}
            style={{ width: '100%', height: '100%' }}
            mapStyle="https://tiles.openfreemap.org/styles/positron"
          >
            <Marker longitude={location.lng} latitude={location.lat} anchor="center">
              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{
                  position: 'absolute',
                  width: 56, height: 56, borderRadius: '50%',
                  background: 'rgba(59,130,246,0.2)',
                  animation: 'busPulse 2s ease-out infinite',
                  '@keyframes busPulse': {
                    '0%': { transform: 'scale(0.7)', opacity: 1 },
                    '100%': { transform: 'scale(1.4)', opacity: 0 },
                  },
                }} />
                <Box sx={{
                  width: 40, height: 40, borderRadius: '50%', background: '#3b82f6',
                  border: '3px solid white',
                  boxShadow: '0 2px 12px rgba(59,130,246,0.5)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 1, fontSize: '1.1rem',
                }}>
                  🚌
                </Box>
              </Box>
            </Marker>
            {childStopPos && (
              <Marker longitude={childStopPos.lng} latitude={childStopPos.lat} anchor="bottom">
                <Box sx={{
                  width: 26, height: 26, borderRadius: '50% 50% 50% 0', background: NAV_BG,
                  transform: 'rotate(-45deg)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid white', boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                }}>
                  <HomeIcon sx={{ fontSize: 14, color: 'white', transform: 'rotate(45deg)' }} />
                </Box>
              </Marker>
            )}
          </Map>
        </Box>
      )}
    </Box>
  );
}
